import {useQueryClient} from "@tanstack/react-query";
import {useAtom} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

import {
  CAMPAIGN_KEYS,
  useCampaignActiveApplicationsQuery,
  useCampaignReportingApplicationsQuery,
} from "@/api/campaignApi";
import {useRevertToRevisionMutation, useSubmissionQuery} from "@/api/submissionApi";
import LinkIcon from "@/assets/colorable/link.svg?react";
import WarningTriangleIcon from "@/assets/colorable/warning_triangle.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import MatchRatioBar from "@/components/ContentApproval/MatchRatioBar";
import {useManagementCampaign} from "@/store/campaignManagement";
import {requestTakedownModalAtom} from "@/store/jotaiAtoms";

const DEFAULT_TAKEDOWN_MESSAGE = {
  de: "Hallo {{first_name}} 👋\n\nWir haben festgestellt, dass der veröffentlichte Beitrag vom ursprünglich eingereichten und genehmigten Inhalt abweicht.\n\nKönntest du den aktuellen Beitrag bitte löschen und die genehmigte Version erneut hochladen? Das hilft uns, die Kampagne konsistent zu halten.\n\nVielen Dank! 🙏",
  en: "Hi {{first_name}} 👋\n\nWe noticed that the published post looks different from the content you originally submitted for approval.\n\nCould you please take down the current post and re-upload the approved version? This helps us keep the campaign consistent.\n\nThank you so much! 🙏",
};

const RequestTakedownModal: FC = () => {
  const {t} = useTranslation(["content_approval", "common"]);
  const campaign = useManagementCampaign();
  const [submissionId, setSubmissionId] = useAtom(requestTakedownModalAtom);
  const [messageLang, setMessageLang] = useState<"de" | "en">("en");
  const [message, setMessage] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const queryClient = useQueryClient();

  const {data: submission, isLoading} = useSubmissionQuery(submissionId);
  const {mutate: revertToRevision, isPending} = useRevertToRevisionMutation(submissionId!);

  const {data: activeApplications} = useCampaignActiveApplicationsQuery({campaignId: campaign.id, searchParams: ""});
  const {data: reportingApplications} = useCampaignReportingApplicationsQuery({searchParam: ""});

  const appId = submission?.campaign_application;
  const matchingApp =
    activeApplications?.find(app => app.id === appId) ?? reportingApplications?.find(app => app.id === appId);
  const firstName = matchingApp?.influencer?.user?.first_name ?? "";

  const fillName = (template: string) => template.replace("{{first_name}}", firstName || "{{first_name}}");

  const onClose = () => setSubmissionId(null);
  const onSuccess = () => {
    queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, "")});
    onClose();
  };

  const switchLanguage = (lang: "de" | "en") => {
    setMessageLang(lang);
    setMessage(fillName(DEFAULT_TAKEDOWN_MESSAGE[lang]));
  };

  const postSyncComparison = submission?.post_sync_comparison;
  const publishedUrl = postSyncComparison?.posted_urls?.[0];

  return (
    <Modal
      backgroundClassName="max-w-2xl max-h-[90vh]"
      customBottomRow={<></>}
      isLoading={isLoading || isPending}
      modalTitle={t("mismatch_title")}
      onClose={onClose}>
      <div className="border-warning-sand bg-warning-light-yellow flex gap-3 rounded-lg border p-4">
        <WarningTriangleIcon className="text-warning-dark-yellow mt-0.5 size-4 shrink-0" />
        <span className="text-warning-dark-yellow text-sm">{t("mismatch_warning")}</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-neutral-black text-sm font-semibold">{t("mismatch_approved_content")}</span>
          <div className="bg-neutral-black aspect-3/4 overflow-hidden rounded-xl">
            {submission?.media_type === "VIDEO" && submission.media_file ? (
              <video className="h-full w-full object-cover" muted preload="metadata" src={submission.media_file} />
            ) : submission?.thumbnail_file ? (
              <img alt="" className="h-full w-full object-cover" src={submission.thumbnail_file} />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span className="text-neutral-grey-dark text-xs">{t("mismatch_approved_content")}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-neutral-black text-sm font-semibold">{t("published_content")}</span>
          <div className="bg-neutral-grey-lightest border-neutral-grey-light-dark flex aspect-3/4 flex-col items-center justify-center gap-3 rounded-xl border p-4">
            {publishedUrl ? (
              <>
                <p className="text-neutral-grey-dark text-center text-xs break-all">{publishedUrl}</p>
                <a
                  className="border-primary-dark-solid text-primary-dark-solid flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold"
                  href={publishedUrl}
                  rel="noopener noreferrer"
                  target="_blank">
                  <LinkIcon className="size-3" />
                  {t("mismatch_view_post")}
                </a>
              </>
            ) : (
              <span className="text-neutral-grey-dark text-xs">{t("published_content")}</span>
            )}
          </div>
        </div>
      </div>

      {postSyncComparison &&
        (postSyncComparison.visual_match_ratio != null || postSyncComparison.audio_match_ratio != null) && (
          <div className="flex flex-col gap-2">
            {postSyncComparison.visual_match_ratio != null && (
              <MatchRatioBar label={t("mismatch_visual_match")} ratio={postSyncComparison.visual_match_ratio} />
            )}
            {postSyncComparison.audio_match_ratio != null && (
              <MatchRatioBar label={t("mismatch_audio_match")} ratio={postSyncComparison.audio_match_ratio} />
            )}
          </div>
        )}

      {isComposing ? (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-neutral-black text-sm font-semibold">{t("mismatch_message_label")}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-neutral-grey-dark text-xs">{t("mismatch_message_language")}</span>
                <select
                  className="border-neutral-grey-light-dark text-neutral-black rounded border bg-white py-0.5 pr-5 pl-1.5 text-xs font-semibold"
                  onChange={e => switchLanguage(e.target.value as "de" | "en")}
                  value={messageLang}>
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
            </div>
            <AutosizeTextarea minRows={4} onChange={e => setMessage(e.target.value)} value={message} />
          </div>
          <div className="flex justify-between gap-3">
            <Button buttonStyle="White" className="px-5 py-2.5" onClick={() => setIsComposing(false)}>
              {t("common:cancel")}
            </Button>
            <Button
              buttonStyle="Danger"
              className="px-5 py-2.5"
              isDisabled={!message.trim()}
              isLoading={isPending}
              onClick={() => revertToRevision({message}, {onSuccess})}>
              {t("mismatch_send_and_request_takedown")}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex justify-between gap-3">
          <Button buttonStyle="Green" className="px-5 py-2.5" onClick={onClose}>
            {t("mismatch_mark_as_resolved")}
          </Button>
          <Button
            buttonStyle="Danger"
            className="px-5 py-2.5"
            onClick={() => {
              setMessage(fillName(DEFAULT_TAKEDOWN_MESSAGE[messageLang]));
              setIsComposing(true);
            }}>
            {t("mismatch_request_takedown")}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default RequestTakedownModal;
